/**
 * LyteWork Name Badge Customizer v1.2
 * Fixes: preview info display, full color auto-rect/rounded, cart price+image, 
 *        logo layout moved to logo tab, html2canvas integration, trigger icon
 */

console.log('%c✅ Name Badge Customizer v1.2 LOADED', 'background: #28a745; color: white; padding: 5px;');

class NameBadgeCustomizer {
    constructor() {
        this.config = window.lw_namebadge_config || {};
        this.state = {
            size: "3x1.5", shape: "rectangle", corners: "square",
            mode: "engraved", engravedColor: this.config.engraved_colors?.[0] || null,
            fullColorBgType: "solid", fullColorBgSolid: "#FFFFFF",
            fullColorGradient: { type: "linear", color1: "#003366", color2: "#66CCFF", angle: 135 },
            fullColorBgImage: null, layout: "logo-top",
            hasLogo: false, logoData: null, logoFileName: null,
            logoSize: 100, // percentage, default 100%
            name: "", nameFont: "Arial, sans-serif", nameSize: 18, nameBold: false, nameItalic: false, nameColor: "#000000",
            title: "", titleFont: "Arial, sans-serif", titleSize: 11, titleBold: false, titleItalic: false, titleColor: "#000000",
            fastener: "pin", quantity: 1, bulkMode: "identical", bulkData: [{ name: '', title: '' }],
            unitPrice: 0, totalPrice: 0,
            variantId: null
        };
        this.modal = null;
        this.isCapturing = false;
        this.init();
    }

    init() {
        this.attachTrigger();
        this.calculateDefaultTextSizes();
        this.loadHtml2Canvas();
        // Get variant ID on init
        this.state.variantId = this.getVariantId();
    }

    // === LOAD html2canvas ===
    loadHtml2Canvas() {
        if (typeof html2canvas !== 'undefined') {
            console.log('html2canvas already loaded');
            return;
        }
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
        script.onload = () => console.log('html2canvas loaded successfully');
        script.onerror = () => console.warn('html2canvas failed to load - cart previews will use fallback');
        document.head.appendChild(script);
    }

    getVariantId() {
        // PRIORITY 1: Use variant ID from config (set by Liquid - most reliable!)
        if (this.config.variantId) {
            console.log('Using variant ID from config:', this.config.variantId);
            return String(this.config.variantId);
        }
        
        // PRIORITY 2: Try to find in the MAIN product form only (not recommended products)
        const productSection = document.querySelector('.product__info-wrapper, .product-info, .product-single, [data-section-type="product"], .product-template, main .product');
        if (productSection) {
            const form = productSection.querySelector('form[action*="/cart/add"]');
            if (form) {
                const variantInput = form.querySelector('input[name="id"][type="hidden"]') ||
                                    form.querySelector('select[name="id"]') ||
                                    form.querySelector('input[name="id"]:checked') ||
                                    form.querySelector('input[name="id"]');
                if (variantInput?.value) {
                    console.log('Using variant ID from product section form:', variantInput.value);
                    return variantInput.value;
                }
            }
        }
        
        // PRIORITY 3: URL parameter
        const urlParams = new URLSearchParams(window.location.search);
        const variantFromUrl = urlParams.get('variant');
        if (variantFromUrl) {
            console.log('Using variant ID from URL:', variantFromUrl);
            return variantFromUrl;
        }
        
        // PRIORITY 4: Shopify analytics (fallback)
        if (window.ShopifyAnalytics?.meta?.selectedVariantId) {
            console.log('Using variant ID from ShopifyAnalytics:', window.ShopifyAnalytics.meta.selectedVariantId);
            return String(window.ShopifyAnalytics.meta.selectedVariantId);
        }
        
        console.error('Could not find variant ID!');
        return null;
    }

    calculateDefaultTextSizes() {
        const sizeConfig = this.config.sizes?.find(s => s.id === this.state.size);
        if (sizeConfig) {
            const h = sizeConfig.height * 72;
            this.state.nameSize = Math.round(h * (this.config.text_defaults?.name_size_ratio || 1/6));
            this.state.titleSize = Math.round(h * (this.config.text_defaults?.title_size_ratio || 1/10));
        }
    }

    attachTrigger() {
        const check = setInterval(() => {
            const btn = document.querySelector('.lw-namebadge-trigger');
            if (btn && !btn.hasAttribute('data-attached')) {
                btn.addEventListener('click', e => { e.preventDefault(); this.open(); });
                btn.setAttribute('data-attached', 'true');
                clearInterval(check);
            }
        }, 100);
        setTimeout(() => clearInterval(check), 5000);
    }

    open() {
        // Refresh variant ID
        this.state.variantId = this.getVariantId();
        this.buildModal();
        this.syncBulkData();
        this.updatePreview();
        this.updatePrice();
        this.lockBodyScroll();
    }

    close() {
        if (this.modal) {
            this.modal.classList.remove('active');
            this.unlockBodyScroll();
            setTimeout(() => { 
                if (this.modal) {
                    this.modal.remove(); 
                    this.modal = null; 
                }
            }, 300);
        }
    }

    lockBodyScroll() {
        this.scrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
    }

    unlockBodyScroll() {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        window.scrollTo(0, this.scrollY || 0);
    }

    buildModal() {
        this.modal = document.createElement('div');
        this.modal.className = 'lw-namebadge-modal';
        this.modal.innerHTML = `
            <div class="lw-namebadge-overlay"></div>
            <div class="lw-namebadge-container">
                <header class="lw-namebadge-header">
                    <div class="lw-header-left">
                        <img src="https://cdn.shopify.com/s/files/1/0590/2244/2570/files/Lw_logo_04_Artboard_2_Copy_3.png" class="lw-logo" alt="LyteWork">
                        <div><h2>Name Badge Designer</h2><p class="lw-variant-subtitle">Custom Name Badges</p></div>
                    </div>
                    <button class="lw-namebadge-close">&times;</button>
                </header>
                <div class="lw-namebadge-body">
                    <div class="lw-namebadge-preview-section">
                        <div class="lw-badge-preview-container">
                            <div class="lw-badge-preview rectangle" id="badgePreview">
                                <div class="lw-badge-background" id="badgeBackground"></div>
                                <div class="lw-badge-content layout-logo-top" id="badgeContent">
                                    <div class="lw-badge-logo-area" id="badgeLogoArea" style="display:none;"><img id="badgeLogoImg" src="" alt=""></div>
                                    <div class="lw-badge-text-area" id="badgeTextArea">
                                        <div class="lw-badge-name" id="badgeName">Your Name</div>
                                        <div class="lw-badge-title" id="badgeTitle"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="lw-preview-info" id="previewInfo"></div>
                    </div>
                    <div class="lw-namebadge-options-section">
                        <div class="lw-namebadge-tabs">
                            <button class="lw-namebadge-tab active" data-tab="design">Design</button>
                            <button class="lw-namebadge-tab" data-tab="text">Text</button>
                            <button class="lw-namebadge-tab" data-tab="logo">Logo</button>
                            <button class="lw-namebadge-tab" data-tab="options">Options</button>
                            <button class="lw-namebadge-tab" data-tab="bulk">Bulk</button>
                        </div>
                        <div class="lw-namebadge-tab-content" id="tabContent"></div>
                    </div>
                </div>
                <footer class="lw-namebadge-footer">
                    <div class="lw-footer-left">
                        <div class="lw-unit-price">Per badge: <strong id="unitPriceDisplay">$0.00</strong></div>
                        <div class="lw-total-price">Total: <span id="totalPriceDisplay">$0.00</span></div>
                    </div>
                    <div class="lw-footer-center">
                        <div class="lw-qty-control">
                            <button class="lw-qty-btn" data-action="decrease">&#8722;</button>
                            <input type="number" class="lw-qty-input" id="qtyInput" value="1" min="1">
                            <button class="lw-qty-btn" data-action="increase">+</button>
                        </div>
                    </div>
                    <div class="lw-footer-right">
                        <button class="lw-btn lw-btn-primary lw-add-to-cart" id="addToCartBtn">Add to Cart</button>
                    </div>
                </footer>

                <div class="lw-processing-overlay" id="lw-processing-overlay">
                    <div class="lw-spinner"></div>
                    <p>Generating your preview...</p>
                </div>
            </div>

            <div class="lw-loading" id="lw-loading">
                <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <path id="logo-path-animation" d="M20,80 L50,20 L80,80 M35,50 L65,50" />
                </svg>
                <p>Processing your Perfection...</p>
            </div>`;
        document.body.appendChild(this.modal);
        requestAnimationFrame(() => this.modal.classList.add('active'));
        this.attachGlobalEvents();
        this.switchTab('design');
    }

    attachGlobalEvents() {
        this.modal.querySelector('.lw-namebadge-close').addEventListener('click', () => this.close());
        this.modal.querySelector('.lw-namebadge-overlay').addEventListener('click', () => this.close());
        this.modal.querySelectorAll('.lw-namebadge-tab').forEach(t => t.addEventListener('click', () => this.switchTab(t.dataset.tab)));
        
        // Quantity controls
        this.modal.querySelectorAll('.lw-qty-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const inp = this.modal.querySelector('#qtyInput');
                let v = parseInt(inp.value) + (btn.dataset.action === 'increase' ? 1 : -1);
                inp.value = this.state.quantity = Math.max(1, v);
                this.syncBulkData();
                this.updatePrice();
                const bulkTabActive = this.modal.querySelector('.lw-namebadge-tab[data-tab="bulk"]')?.classList.contains('active');
                if (bulkTabActive) this.switchTab('bulk');
            });
        });
        
        this.modal.querySelector('#qtyInput').addEventListener('change', e => {
            this.state.quantity = Math.max(1, parseInt(e.target.value) || 1);
            e.target.value = this.state.quantity;
            this.syncBulkData();
            this.updatePrice();
            const bulkTabActive = this.modal.querySelector('.lw-namebadge-tab[data-tab="bulk"]')?.classList.contains('active');
            if (bulkTabActive) this.switchTab('bulk');
        });
        
        this.modal.querySelector('#addToCartBtn').addEventListener('click', () => this.addToCart());
    }

    switchTab(tabName) {
        this.modal.querySelectorAll('.lw-namebadge-tab').forEach(t => t.classList.toggle('active', t.dataset.tab === tabName));
        const content = this.modal.querySelector('#tabContent');
        if (tabName === 'design') { content.innerHTML = this.renderDesignTab(); this.attachDesignEvents(); }
        else if (tabName === 'text') { content.innerHTML = this.renderTextTab(); this.attachTextEvents(); }
        else if (tabName === 'logo') { content.innerHTML = this.renderLogoTab(); this.attachLogoEvents(); }
        else if (tabName === 'options') { content.innerHTML = this.renderOptionsTab(); this.attachOptionsEvents(); }
        else if (tabName === 'bulk') { content.innerHTML = this.renderBulkTab(); this.attachBulkEvents(); }
    }

    // === DESIGN TAB ===
    renderDesignTab() {
        const sizes = this.config.sizes || [];
        const curSize = sizes.find(s => s.id === this.state.size);
        const hasOval = curSize?.shapes?.includes('oval');
        const fullColorOK = this.config.full_color_active_sizes?.includes(this.state.size);
        const isFullColor = this.state.mode === 'fullcolor';

        return `<div class="lw-namebadge-tab-pane active">
            <h4 class="lw-section-header">Badge Size</h4>
            <div class="lw-option-grid cols-3">${sizes.map(s => `
                <div class="lw-option-card ${this.state.size === s.id ? 'active' : ''}" data-size="${s.id}">
                    <div class="lw-option-label">${s.label}</div>
                </div>`).join('')}</div>

            <h4 class="lw-section-header">Shape</h4>
            ${isFullColor ? `
                <div class="lw-info-box" style="margin-bottom:12px;">Full color badges are rectangular with rounded corners.</div>
            ` : `
            <div class="lw-option-grid cols-2">
                <div class="lw-option-card ${this.state.shape === 'rectangle' ? 'active' : ''}" data-shape="rectangle">
                    <div class="lw-option-icon">&#9645;</div><div class="lw-option-label">Rectangle</div>
                </div>
                <div class="lw-option-card ${this.state.shape === 'oval' ? 'active' : ''} ${!hasOval ? 'disabled' : ''}" data-shape="oval">
                    <div class="lw-option-icon">&#11053;</div><div class="lw-option-label">Oval</div>
                    ${!hasOval ? '<div class="lw-option-price">N/A this size</div>' : ''}
                </div>
            </div>`}

            ${!isFullColor && this.state.shape === 'rectangle' ? `
            <h4 class="lw-section-header">Corners</h4>
            <div class="lw-option-grid cols-2">
                <div class="lw-option-card ${this.state.corners === 'square' ? 'active' : ''}" data-corners="square">
                    <div class="lw-option-label">Square</div><div class="lw-option-price">Included</div>
                </div>
                <div class="lw-option-card ${this.state.corners === 'rounded' ? 'active' : ''}" data-corners="rounded">
                    <div class="lw-option-label">Rounded</div><div class="lw-option-price">+$${(this.config.add_ons?.rounded_corners || 0.50).toFixed(2)}</div>
                </div>
            </div>` : ''}

            <h4 class="lw-section-header">Badge Type</h4>
            <div class="lw-option-grid cols-2">
                <div class="lw-option-card ${this.state.mode === 'engraved' ? 'active' : ''}" data-mode="engraved">
                    <div class="lw-option-icon">&#9998;</div><div class="lw-option-label">Engraved</div>
                </div>
                <div class="lw-option-card ${isFullColor ? 'active' : ''} ${!fullColorOK ? 'disabled' : ''}" data-mode="fullcolor">
                    <div class="lw-option-icon">&#127912;</div><div class="lw-option-label">Full Color</div>
                    <div class="lw-option-price">${fullColorOK ? '+$' + (this.config.add_ons?.full_color || 2).toFixed(2) : '3\u00d71\u00bd only'}</div>
                </div>
            </div>

            ${isFullColor ? this.renderFullColorOptions() : this.renderEngravedColors()}

            ${this.renderPricingTable()}
        </div>`;
    }

    renderEngravedColors() {
        return `<h4 class="lw-section-header">Color</h4>
        <div class="lw-color-grid">${(this.config.engraved_colors || []).map(c => `
            <div class="lw-color-swatch ${this.state.engravedColor?.id === c.id ? 'active' : ''}" data-color-id="${c.id}">
                <div class="lw-color-preview" style="background:${c.surface_hex};${c.texture_image ? 'background-image:url(' + c.texture_image + ');background-size:cover;' : ''}${c.id === 'wood-brown' ? 'background-image:url(data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="#D4B896" width="100" height="100"/><g fill="#C4A880" opacity="0.3"><path d="M0 10 Q25 8 50 12 Q75 9 100 11 L100 13 Q75 11 50 14 Q25 10 0 12Z"/><path d="M0 30 Q25 28 50 32 Q75 29 100 31 L100 33 Q75 31 50 34 Q25 30 0 32Z"/><path d="M0 50 Q25 48 50 52 Q75 49 100 51 L100 53 Q75 51 50 54 Q25 50 0 52Z"/><path d="M0 70 Q25 68 50 72 Q75 69 100 71 L100 73 Q75 71 50 74 Q25 70 0 72Z"/><path d="M0 90 Q25 88 50 92 Q75 89 100 91 L100 93 Q75 91 50 94 Q25 90 0 92Z"/></g></svg>') + ');background-size:cover;' : ''}">
                    <span style="color:${c.engrave_hex};font-size:11px;font-weight:bold;display:flex;align-items:center;justify-content:center;height:100%;">ABC</span>
                </div>
                <div class="lw-color-name">${c.name}</div>
            </div>`).join('')}</div>`;
    }

    renderFullColorOptions() {
        const bgType = this.state.fullColorBgType;
        const solids = this.config.full_color_backgrounds?.solid_colors || [];
        const imgs = this.config.full_color_backgrounds?.background_images || [];

        return `<h4 class="lw-section-header">Background Type</h4>
        <div class="lw-option-grid cols-3">
            <div class="lw-option-card ${bgType === 'solid' ? 'active' : ''}" data-bg-type="solid"><div class="lw-option-label">Solid</div></div>
            <div class="lw-option-card ${bgType === 'gradient' ? 'active' : ''}" data-bg-type="gradient"><div class="lw-option-label">Gradient</div></div>
            <div class="lw-option-card ${bgType === 'image' ? 'active' : ''}" data-bg-type="image"><div class="lw-option-label">Image</div></div>
        </div>

        ${bgType === 'solid' ? `
        <h4 class="lw-section-header">Background Color</h4>
        <div class="lw-color-grid">${solids.map(c => `
            <div class="lw-color-swatch ${this.state.fullColorBgSolid === c.hex ? 'active' : ''}" data-solid-color="${c.hex}">
                <div class="lw-color-preview" style="background:${c.hex};"></div>
                <div class="lw-color-name">${c.name}</div>
            </div>`).join('')}</div>
        <div class="lw-input-row"><label class="lw-input-label">Custom Color</label>
            <input type="color" id="customBgColor" value="${this.state.fullColorBgSolid}" style="width:100%;height:40px;border:none;cursor:pointer;">
        </div>` : ''}

        ${bgType === 'gradient' ? `
        <div class="lw-gradient-builder">
            <div class="lw-gradient-preview" id="gradientPreview" style="${this.getGradientCSS()}"></div>
            <div class="lw-option-grid cols-2" style="margin:12px 0;">
                <div class="lw-option-card ${this.state.fullColorGradient.type === 'linear' ? 'active' : ''}" data-grad-type="linear"><div class="lw-option-label">Linear</div></div>
                <div class="lw-option-card ${this.state.fullColorGradient.type === 'radial' ? 'active' : ''}" data-grad-type="radial"><div class="lw-option-label">Radial</div></div>
            </div>
            <div style="display:flex;gap:12px;flex-wrap:wrap;align-items:center;">
                <label>Color 1: <input type="color" id="gradColor1" value="${this.state.fullColorGradient.color1}"></label>
                <label>Color 2: <input type="color" id="gradColor2" value="${this.state.fullColorGradient.color2}"></label>
                ${this.state.fullColorGradient.type === 'linear' ? `<label>Angle: <input type="number" id="gradAngle" value="${this.state.fullColorGradient.angle}" min="0" max="360" style="width:60px;">&deg;</label>` : ''}
            </div>
        </div>` : ''}

        ${bgType === 'image' ? `
        <h4 class="lw-section-header">Background Image</h4>
        <div class="lw-bg-image-grid">${imgs.map(i => `
            <div class="lw-bg-image-option ${this.state.fullColorBgImage?.id === i.id ? 'active' : ''}" data-bg-img="${i.id}">
                <img src="${i.thumbnail}" alt="${i.name}" class="lw-bg-image-thumb">
                <div class="lw-bg-image-name">${i.name}</div>
            </div>`).join('')}</div>` : ''}`;
    }

    renderPricingTable() {
        // For full color, pricing uses rectangle shape always
        const pricingShape = this.state.mode === 'fullcolor' ? 'rectangle' : this.state.shape;
        const pricing = this.config.base_pricing?.[this.state.size]?.[pricingShape] || [];
        const labels = ['1-5', '6-25', '26-99', '100+'];
        const breaks = this.config.quantity_breaks || [1, 6, 26, 100];
        const q = this.state.quantity;

        return `<div class="lw-pricing-table">
            <div class="lw-pricing-title">Volume Pricing (per badge)</div>
            <div class="lw-pricing-tiers">${pricing.map((p, i) => {
                const active = (i === 0 && q < breaks[1]) || (i === 1 && q >= breaks[1] && q < breaks[2]) ||
                               (i === 2 && q >= breaks[2] && q < breaks[3]) || (i === 3 && q >= breaks[3]);
                return `<div class="lw-pricing-tier ${active ? 'active' : ''}"><span class="lw-tier-qty">${labels[i]}:</span> <span class="lw-tier-price">$${p.toFixed(2)}</span></div>`;
            }).join('')}</div>
        </div>`;
    }

    getGradientCSS() {
        const g = this.state.fullColorGradient;
        return g.type === 'linear' ? `background:linear-gradient(${g.angle}deg,${g.color1},${g.color2});`
                                   : `background:radial-gradient(circle,${g.color1},${g.color2});`;
    }

    attachDesignEvents() {
        this.modal.querySelectorAll('[data-size]').forEach(el => el.addEventListener('click', () => {
            this.state.size = el.dataset.size;
            this.calculateDefaultTextSizes();
            const cfg = this.config.sizes?.find(s => s.id === this.state.size);
            if (!cfg?.shapes?.includes(this.state.shape)) this.state.shape = 'rectangle';
            if (this.state.mode === 'fullcolor' && !this.config.full_color_active_sizes?.includes(this.state.size)) this.state.mode = 'engraved';
            // Re-enforce full color constraints
            if (this.state.mode === 'fullcolor') {
                this.state.shape = 'rectangle';
                this.state.corners = 'rounded';
            }
            this.switchTab('design'); this.updatePreview(); this.updatePrice();
        }));
        this.modal.querySelectorAll('[data-shape]').forEach(el => el.addEventListener('click', () => {
            if (el.classList.contains('disabled')) return;
            if (this.state.mode === 'fullcolor') return; // locked for full color
            this.state.shape = el.dataset.shape;
            if (this.state.shape === 'oval') this.state.corners = 'square';
            this.switchTab('design'); this.updatePreview(); this.updatePrice();
        }));
        this.modal.querySelectorAll('[data-corners]').forEach(el => el.addEventListener('click', () => {
            if (this.state.mode === 'fullcolor') return; // locked for full color
            this.state.corners = el.dataset.corners;
            this.switchTab('design'); this.updatePreview(); this.updatePrice();
        }));
        this.modal.querySelectorAll('[data-mode]').forEach(el => el.addEventListener('click', () => {
            if (el.classList.contains('disabled')) return;
            this.state.mode = el.dataset.mode;
            // FIX: Full color badges are always rectangular with rounded corners
            if (this.state.mode === 'fullcolor') {
                this.state.shape = 'rectangle';
                this.state.corners = 'rounded';
            }
            this.switchTab('design'); this.updatePreview(); this.updatePrice();
        }));
        this.modal.querySelectorAll('[data-color-id]').forEach(el => el.addEventListener('click', () => {
            this.state.engravedColor = this.config.engraved_colors?.find(c => c.id === el.dataset.colorId);
            this.switchTab('design'); this.updatePreview();
        }));
        this.modal.querySelectorAll('[data-bg-type]').forEach(el => el.addEventListener('click', () => {
            this.state.fullColorBgType = el.dataset.bgType;
            this.switchTab('design'); this.updatePreview();
        }));
        this.modal.querySelectorAll('[data-solid-color]').forEach(el => el.addEventListener('click', () => {
            this.state.fullColorBgSolid = el.dataset.solidColor;
            this.switchTab('design'); this.updatePreview();
        }));
        const customBg = this.modal.querySelector('#customBgColor');
        if (customBg) customBg.addEventListener('input', e => { this.state.fullColorBgSolid = e.target.value; this.updatePreview(); });
        this.modal.querySelectorAll('[data-grad-type]').forEach(el => el.addEventListener('click', () => {
            this.state.fullColorGradient.type = el.dataset.gradType;
            this.switchTab('design'); this.updatePreview();
        }));
        ['gradColor1', 'gradColor2', 'gradAngle'].forEach(id => {
            const el = this.modal.querySelector('#' + id);
            if (el) el.addEventListener('input', e => {
                if (id === 'gradColor1') this.state.fullColorGradient.color1 = e.target.value;
                else if (id === 'gradColor2') this.state.fullColorGradient.color2 = e.target.value;
                else this.state.fullColorGradient.angle = parseInt(e.target.value) || 0;
                const prev = this.modal.querySelector('#gradientPreview');
                if (prev) prev.style.cssText = this.getGradientCSS();
                this.updatePreview();
            });
        });
        this.modal.querySelectorAll('[data-bg-img]').forEach(el => el.addEventListener('click', () => {
            this.state.fullColorBgImage = this.config.full_color_backgrounds?.background_images?.find(i => i.id === el.dataset.bgImg);
            this.switchTab('design'); this.updatePreview();
        }));
    }

    // === TEXT TAB ===
    renderTextTab() {
        const fonts = this.config.fonts || [];
        const isFull = this.state.mode === 'fullcolor';
        const presets = this.config.text_color_presets || [];

        return `<div class="lw-namebadge-tab-pane active">
            <h4 class="lw-section-header">Name</h4>
            <div class="lw-input-row"><input type="text" class="lw-input" id="nameInput" value="${this.escapeHtml(this.state.name)}" placeholder="Enter name..." maxlength="50"></div>
            <div class="lw-input-group">
                <select class="lw-select" id="nameFontSelect" style="flex:2;">${fonts.map(f => `<option value="${f.value}" ${this.state.nameFont === f.value ? 'selected' : ''}>${f.name}</option>`).join('')}</select>
                <div class="lw-text-controls">
                    <div class="lw-size-control">
                        <button class="lw-size-btn" data-target="name" data-action="decrease">&#8722;</button>
                        <span class="lw-size-display" id="nameSizeDisplay">${this.state.nameSize}pt</span>
                        <button class="lw-size-btn" data-target="name" data-action="increase">+</button>
                    </div>
                    <button class="lw-style-btn ${this.state.nameBold ? 'active' : ''}" data-target="name" data-style="bold"><b>B</b></button>
                    <button class="lw-style-btn ${this.state.nameItalic ? 'active' : ''}" data-target="name" data-style="italic"><i>I</i></button>
                </div>
            </div>
            ${isFull ? `<div class="lw-input-row"><label class="lw-input-label">Text Color</label>
                <div style="display:flex;gap:8px;align-items:center;">
                    <input type="color" id="nameColorPicker" value="${this.state.nameColor}" style="width:40px;height:32px;border:none;">
                    ${presets.slice(0,6).map(c => `<div class="lw-mini-swatch" data-color="${c.hex}" data-target="name" style="width:24px;height:24px;background:${c.hex};border:2px solid ${this.state.nameColor===c.hex?'#233354':'#ddd'};border-radius:4px;cursor:pointer;" title="${c.name}"></div>`).join('')}
                </div>
            </div>` : ''}

            <h4 class="lw-section-header">Title <span style="font-weight:normal;font-size:12px;color:#666;">(Optional)</span></h4>
            <div class="lw-input-row"><input type="text" class="lw-input" id="titleInput" value="${this.escapeHtml(this.state.title)}" placeholder="Enter title (optional)..." maxlength="50"></div>
            <div class="lw-input-group">
                <select class="lw-select" id="titleFontSelect" style="flex:2;">${fonts.map(f => `<option value="${f.value}" ${this.state.titleFont === f.value ? 'selected' : ''}>${f.name}</option>`).join('')}</select>
                <div class="lw-text-controls">
                    <div class="lw-size-control">
                        <button class="lw-size-btn" data-target="title" data-action="decrease">&#8722;</button>
                        <span class="lw-size-display" id="titleSizeDisplay">${this.state.titleSize}pt</span>
                        <button class="lw-size-btn" data-target="title" data-action="increase">+</button>
                    </div>
                    <button class="lw-style-btn ${this.state.titleBold ? 'active' : ''}" data-target="title" data-style="bold"><b>B</b></button>
                    <button class="lw-style-btn ${this.state.titleItalic ? 'active' : ''}" data-target="title" data-style="italic"><i>I</i></button>
                </div>
            </div>
            ${isFull ? `<div class="lw-input-row"><label class="lw-input-label">Text Color</label>
                <div style="display:flex;gap:8px;align-items:center;">
                    <input type="color" id="titleColorPicker" value="${this.state.titleColor}" style="width:40px;height:32px;border:none;">
                    ${presets.slice(0,6).map(c => `<div class="lw-mini-swatch" data-color="${c.hex}" data-target="title" style="width:24px;height:24px;background:${c.hex};border:2px solid ${this.state.titleColor===c.hex?'#233354':'#ddd'};border-radius:4px;cursor:pointer;" title="${c.name}"></div>`).join('')}
                </div>
            </div>` : ''}
            <div class="lw-info-box" style="margin-top:20px;"><strong>Tip:</strong> Leave the title blank if you only need a name.</div>
        </div>`;
    }

    // Helper to escape HTML in input values
    escapeHtml(str) {
        if (!str) return '';
        return str.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    }

    attachTextEvents() {
        const nameInp = this.modal.querySelector('#nameInput');
        if (nameInp) nameInp.addEventListener('input', e => { 
            this.state.name = e.target.value; 
            if (this.state.bulkData.length > 0) this.state.bulkData[0].name = e.target.value;
            this.updatePreview(); 
        });
        const titleInp = this.modal.querySelector('#titleInput');
        if (titleInp) titleInp.addEventListener('input', e => { 
            this.state.title = e.target.value; 
            if (this.state.bulkData.length > 0) this.state.bulkData[0].title = e.target.value;
            this.updatePreview(); 
        });
        const nameFont = this.modal.querySelector('#nameFontSelect');
        if (nameFont) nameFont.addEventListener('change', e => { this.state.nameFont = e.target.value; this.updatePreview(); });
        const titleFont = this.modal.querySelector('#titleFontSelect');
        if (titleFont) titleFont.addEventListener('change', e => { this.state.titleFont = e.target.value; this.updatePreview(); });

        this.modal.querySelectorAll('.lw-size-btn').forEach(btn => btn.addEventListener('click', () => {
            const t = btn.dataset.target, a = btn.dataset.action, k = t + 'Size';
            const min = this.config.text_defaults?.min_font_size || 8, max = this.config.text_defaults?.max_font_size || 72;
            this.state[k] = Math.max(min, Math.min(max, this.state[k] + (a === 'increase' ? 2 : -2)));
            const disp = this.modal.querySelector(`#${t}SizeDisplay`);
            if (disp) disp.textContent = this.state[k] + 'pt';
            this.updatePreview();
        }));
        this.modal.querySelectorAll('.lw-style-btn').forEach(btn => btn.addEventListener('click', () => {
            const t = btn.dataset.target, s = btn.dataset.style, k = t + (s === 'bold' ? 'Bold' : 'Italic');
            this.state[k] = !this.state[k];
            btn.classList.toggle('active', this.state[k]);
            this.updatePreview();
        }));
        const nameColor = this.modal.querySelector('#nameColorPicker');
        if (nameColor) nameColor.addEventListener('input', e => { this.state.nameColor = e.target.value; this.updatePreview(); });
        const titleColor = this.modal.querySelector('#titleColorPicker');
        if (titleColor) titleColor.addEventListener('input', e => { this.state.titleColor = e.target.value; this.updatePreview(); });
        this.modal.querySelectorAll('.lw-mini-swatch').forEach(sw => sw.addEventListener('click', () => {
            const t = sw.dataset.target, c = sw.dataset.color;
            this.state[t + 'Color'] = c;
            this.switchTab('text');
            this.updatePreview();
        }));
    }

    // === LOGO TAB === (with layout selector moved here)
    renderLogoTab() {
        const layouts = this.config.layouts || [];
        
        return `<div class="lw-namebadge-tab-pane active">
            <h4 class="lw-section-header">Upload Logo <span style="font-weight:normal;font-size:12px;color:#666;">(Optional)</span></h4>
            <div class="lw-upload-area ${this.state.hasLogo ? 'has-file' : ''}" id="logoUploadArea">
                ${this.state.hasLogo ? `
                    <div style="text-align:center;">
                        <img src="${this.state.logoData}" class="lw-logo-preview" id="logoPreviewImg" style="max-width:150px;max-height:100px;margin-bottom:12px;">
                        <div style="font-size:13px;color:#666;margin-bottom:8px;">${this.escapeHtml(this.state.logoFileName)}</div>
                        <button class="lw-btn lw-btn-sm" id="removeLogoBtn" style="background:#dc3545;color:white;">Remove Logo</button>
                    </div>
                ` : `
                    <div class="lw-upload-prompt">
                        <div class="lw-upload-icon">&#128228;</div>
                        <div class="lw-upload-text">Click to Upload Logo</div>
                        <div class="lw-upload-hint">PNG, JPG, or SVG recommended</div>
                    </div>
                `}
            </div>
            <input type="file" id="logoFileInput" accept="image/*" style="display:none;">
            
            ${this.state.hasLogo ? `
                <h4 class="lw-section-header">Logo Size</h4>
                <div class="lw-input-row" style="display:flex;align-items:center;gap:12px;">
                    <input type="range" id="logoSizeSlider" min="50" max="150" value="${this.state.logoSize}" style="flex:1;">
                    <span id="logoSizeDisplay">${this.state.logoSize}%</span>
                </div>
            ` : ''}
            
            <h4 class="lw-section-header">Logo Position</h4>
            <div class="lw-layout-grid">${layouts.map(l => `
                <div class="lw-layout-option ${this.state.layout === l.id ? 'active' : ''}" data-layout="${l.id}" title="${l.description}">
                    <div class="lw-layout-icon">${l.icon}</div><div class="lw-layout-name">${l.name}</div>
                </div>`).join('')}</div>
            
            ${this.state.mode === 'engraved' ? `
                <div class="lw-info-box"><strong>Note:</strong> For engraved badges, your logo will appear in the engraved color (${this.state.engravedColor?.name || 'selected color'}).</div>
            ` : `
                <div class="lw-info-box"><strong>Note:</strong> For full-color badges, your logo will print in its original colors.</div>
            `}
        </div>`;
    }

    attachLogoEvents() {
        const area = this.modal.querySelector('#logoUploadArea');
        const input = this.modal.querySelector('#logoFileInput');
        if (area && input) {
            area.addEventListener('click', () => { if (!this.state.hasLogo) input.click(); });
            input.addEventListener('change', e => {
                const file = e.target.files[0];
                if (file) {
                    const reader = new FileReader();
                    reader.onload = ev => {
                        this.state.logoData = ev.target.result;
                        this.state.logoFileName = file.name;
                        this.state.hasLogo = true;
                        this.switchTab('logo');
                        this.updatePreview();
                    };
                    reader.readAsDataURL(file);
                }
            });
        }
        const removeBtn = this.modal.querySelector('#removeLogoBtn');
        if (removeBtn) removeBtn.addEventListener('click', e => {
            e.stopPropagation();
            this.state.hasLogo = false;
            this.state.logoData = null;
            this.state.logoFileName = null;
            this.state.logoSize = 100;
            this.switchTab('logo');
            this.updatePreview();
        });
        
        // Logo size slider
        const sizeSlider = this.modal.querySelector('#logoSizeSlider');
        const sizeDisplay = this.modal.querySelector('#logoSizeDisplay');
        if (sizeSlider) {
            sizeSlider.addEventListener('input', e => {
                this.state.logoSize = parseInt(e.target.value);
                if (sizeDisplay) sizeDisplay.textContent = this.state.logoSize + '%';
                this.updatePreview();
            });
        }
        
        // Layout selector (now in Logo tab)
        this.modal.querySelectorAll('[data-layout]').forEach(el => el.addEventListener('click', () => {
            this.state.layout = el.dataset.layout;
            this.switchTab('logo');
            this.updatePreview();
        }));
    }

    // === OPTIONS TAB ===
    renderOptionsTab() {
        const fasteners = this.config.fasteners || [];
        return `<div class="lw-namebadge-tab-pane active">
            <h4 class="lw-section-header">Fastener Type</h4>
            <div class="lw-fastener-grid">${fasteners.map(f => `
                <div class="lw-fastener-option ${this.state.fastener === f.id ? 'active' : ''}" data-fastener="${f.id}">
                    <img src="${f.image}" alt="${f.name}" class="lw-fastener-image">
                    <div class="lw-fastener-name">${f.name}</div>
                    <div class="lw-fastener-price">${f.price_add > 0 ? '+$' + f.price_add.toFixed(2) : 'Included'}</div>
                </div>
            `).join('')}</div>
            ${this.renderPricingTable()}
        </div>`;
    }

    attachOptionsEvents() {
        this.modal.querySelectorAll('[data-fastener]').forEach(el => el.addEventListener('click', () => {
            this.state.fastener = el.dataset.fastener;
            this.switchTab('options');
            this.updatePrice();
        }));
    }

    // === BULK TAB ===
    renderBulkTab() {
        const isVar = this.state.bulkMode === 'variable';
        
        if (this.state.bulkData.length > 0) {
            this.state.bulkData[0].name = this.state.name;
            this.state.bulkData[0].title = this.state.title;
        }
        
        return `<div class="lw-namebadge-tab-pane active">
            <h4 class="lw-section-header">Order Type</h4>
            <div class="lw-option-grid cols-2">
                <div class="lw-option-card ${!isVar ? 'active' : ''}" data-bulk="identical">
                    <div class="lw-option-label">Same Badge</div>
                    <div class="lw-option-price">All identical</div>
                </div>
                <div class="lw-option-card ${isVar ? 'active' : ''}" data-bulk="variable">
                    <div class="lw-option-label">Variable Data</div>
                    <div class="lw-option-price">Different names</div>
                </div>
            </div>

            ${isVar ? `
                <h4 class="lw-section-header">Badge List (${this.state.bulkData.length} badges)</h4>
                <div class="lw-info-box" style="margin-bottom:12px;">
                    <strong>Badge #1</strong> uses the name/title from the Text tab. Adjust quantity in footer to add more rows.
                </div>
                <div class="lw-bulk-table-wrapper">
                    <table class="lw-bulk-table">
                        <thead><tr><th style="width:40px;">#</th><th>Name</th><th>Title</th></tr></thead>
                        <tbody>${this.state.bulkData.map((row, i) => `
                            <tr>
                                <td style="text-align:center;font-weight:600;">${i + 1}</td>
                                <td><input type="text" class="bulk-name" data-idx="${i}" value="${this.escapeHtml(row.name)}" placeholder="Name" ${i === 0 ? 'readonly style="background:#f0f0f0;"' : ''}></td>
                                <td><input type="text" class="bulk-title" data-idx="${i}" value="${this.escapeHtml(row.title)}" placeholder="Title" ${i === 0 ? 'readonly style="background:#f0f0f0;"' : ''}></td>
                            </tr>
                        `).join('')}</tbody>
                    </table>
                </div>
                <div class="lw-bulk-actions">
                    <button class="lw-btn lw-btn-secondary lw-btn-sm" id="clearBulkBtn">Clear All (Except #1)</button>
                    <label class="lw-btn lw-btn-secondary lw-btn-sm" style="cursor:pointer;">
                        Upload CSV <input type="file" id="csvUpload" accept=".csv,.txt" style="display:none;">
                    </label>
                </div>
                <div class="lw-info-box" style="margin-top:12px;">
                    <strong>CSV Format:</strong> Name,Title (one per line). First line updates Text tab.
                </div>
            ` : `
                <div class="lw-info-box">
                    <strong>Identical Badges:</strong> All ${this.state.quantity} badge(s) will have the same name and title from the Text tab.
                </div>
            `}
        </div>`;
    }

    attachBulkEvents() {
        this.modal.querySelectorAll('[data-bulk]').forEach(el => el.addEventListener('click', () => {
            this.state.bulkMode = el.dataset.bulk;
            this.syncBulkData();
            this.switchTab('bulk');
        }));
        
        this.modal.querySelectorAll('.bulk-name').forEach(inp => {
            const idx = parseInt(inp.dataset.idx);
            if (idx > 0) {
                inp.addEventListener('input', e => { this.state.bulkData[idx].name = e.target.value; });
            }
        });
        
        this.modal.querySelectorAll('.bulk-title').forEach(inp => {
            const idx = parseInt(inp.dataset.idx);
            if (idx > 0) {
                inp.addEventListener('input', e => { this.state.bulkData[idx].title = e.target.value; });
            }
        });
        
        const clearBtn = this.modal.querySelector('#clearBulkBtn');
        if (clearBtn) clearBtn.addEventListener('click', () => {
            this.state.bulkData = this.state.bulkData.map((row, i) => 
                i === 0 ? row : { name: '', title: '' }
            );
            this.switchTab('bulk');
        });
        
        const csvUp = this.modal.querySelector('#csvUpload');
        if (csvUp) csvUp.addEventListener('change', e => {
            const file = e.target.files[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = ev => {
                    const lines = ev.target.result.split(/\r?\n/).filter(l => l.trim());
                    lines.forEach((line, i) => {
                        if (i < this.state.bulkData.length) {
                            const [name, title] = line.split(',').map(s => s.trim());
                            this.state.bulkData[i] = { name: name || '', title: title || '' };
                        }
                    });
                    if (this.state.bulkData.length > 0) {
                        this.state.name = this.state.bulkData[0].name;
                        this.state.title = this.state.bulkData[0].title;
                    }
                    this.switchTab('bulk');
                    this.updatePreview();
                };
                reader.readAsText(file);
            }
        });
    }

    syncBulkData() {
        const target = this.state.quantity;
        if (this.state.bulkData.length === 0) {
            this.state.bulkData.push({ name: this.state.name, title: this.state.title });
        } else {
            this.state.bulkData[0].name = this.state.name;
            this.state.bulkData[0].title = this.state.title;
        }
        while (this.state.bulkData.length < target) this.state.bulkData.push({ name: '', title: '' });
        while (this.state.bulkData.length > target) this.state.bulkData.pop();
    }

    // === PREVIEW ===
    updatePreview() {
        const preview = this.modal?.querySelector('#badgePreview');
        const bg = this.modal?.querySelector('#badgeBackground');
        const content = this.modal?.querySelector('#badgeContent');
        const logoArea = this.modal?.querySelector('#badgeLogoArea');
        const logoImg = this.modal?.querySelector('#badgeLogoImg');
        const nameEl = this.modal?.querySelector('#badgeName');
        const titleEl = this.modal?.querySelector('#badgeTitle');
        const infoEl = this.modal?.querySelector('#previewInfo');

        if (!preview) return;

        // Size
        const sizeConfig = this.config.sizes?.find(s => s.id === this.state.size);
        const w = (sizeConfig?.width || 3) * 80;
        const h = (sizeConfig?.height || 1.5) * 80;
        preview.style.width = w + 'px';
        preview.style.height = h + 'px';

        // Shape & Corners
        preview.className = 'lw-badge-preview ' + this.state.shape;
        if (this.state.shape === 'rectangle') {
            preview.style.borderRadius = this.state.corners === 'rounded' ? '20px' : '0';
        } else {
            preview.style.borderRadius = '50%';
        }

        // Background
        if (this.state.mode === 'engraved') {
            const c = this.state.engravedColor;
            if (c?.texture_image) {
                bg.style.cssText = `background-image:url(${c.texture_image});background-size:cover;`;
            } else if (c?.id === 'wood-brown') {
                const woodGrain = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="#D4B896" width="200" height="200"/><g fill="#C4A880" opacity="0.25"><path d="M0 20 Q50 16 100 22 Q150 18 200 21 L200 24 Q150 21 100 25 Q50 19 0 23Z"/><path d="M0 50 Q50 46 100 52 Q150 48 200 51 L200 54 Q150 51 100 55 Q50 49 0 53Z"/><path d="M0 80 Q50 76 100 82 Q150 78 200 81 L200 84 Q150 81 100 85 Q50 79 0 83Z"/><path d="M0 110 Q50 106 100 112 Q150 108 200 111 L200 114 Q150 111 100 115 Q50 109 0 113Z"/><path d="M0 140 Q50 136 100 142 Q150 138 200 141 L200 144 Q150 141 100 145 Q50 139 0 143Z"/><path d="M0 170 Q50 166 100 172 Q150 168 200 171 L200 174 Q150 171 100 175 Q50 169 0 173Z"/></g></svg>')}`;
                bg.style.cssText = `background:${c.surface_hex};background-image:url("${woodGrain}");background-size:cover;`;
            } else {
                bg.style.cssText = `background:${c?.surface_hex || '#D4AF37'};`;
            }
        } else {
            if (this.state.fullColorBgType === 'solid') {
                bg.style.cssText = `background:${this.state.fullColorBgSolid};`;
            } else if (this.state.fullColorBgType === 'gradient') {
                bg.style.cssText = this.getGradientCSS();
            } else if (this.state.fullColorBgType === 'image' && this.state.fullColorBgImage) {
                bg.style.cssText = `background-image:url(${this.state.fullColorBgImage.url});background-size:cover;background-position:center;`;
            }
        }

        // Layout
        content.className = 'lw-badge-content layout-' + this.state.layout;

        // Logo
        if (this.state.hasLogo && this.state.logoData) {
            logoArea.style.display = 'flex';
            logoImg.src = this.state.logoData;
            
            if (this.state.mode === 'engraved' && this.state.engravedColor) {
                const engraveHex = this.state.engravedColor.engrave_hex || '#000000';
                logoArea.style.position = 'relative';
                logoImg.style.filter = this.getColorFilter(engraveHex);
            } else {
                logoImg.style.filter = 'none';
            }
            
            const isVert = this.state.layout === 'logo-top' || this.state.layout === 'logo-bottom';
            const baseWidth = isVert ? 50 : 33;
            const baseHeight = isVert ? 45 : 80;
            const scale = this.state.logoSize / 100;
            logoArea.style.width = (baseWidth * scale) + '%';
            logoArea.style.height = (baseHeight * scale) + '%';
        } else {
            logoArea.style.display = 'none';
        }

        // Text colors
        const textColor = this.state.mode === 'engraved' ? (this.state.engravedColor?.engrave_hex || '#000') : this.state.nameColor;
        const titleColor = this.state.mode === 'engraved' ? (this.state.engravedColor?.engrave_hex || '#000') : this.state.titleColor;

        // Name
        nameEl.textContent = this.state.name || 'Your Name';
        nameEl.style.fontFamily = this.state.nameFont;
        nameEl.style.fontSize = this.state.nameSize + 'px';
        nameEl.style.fontWeight = this.state.nameBold ? 'bold' : 'normal';
        nameEl.style.fontStyle = this.state.nameItalic ? 'italic' : 'normal';
        nameEl.style.color = textColor;

        // Title
        if (this.state.title) {
            titleEl.style.display = 'block';
            titleEl.textContent = this.state.title;
            titleEl.style.fontFamily = this.state.titleFont;
            titleEl.style.fontSize = this.state.titleSize + 'px';
            titleEl.style.fontWeight = this.state.titleBold ? 'bold' : 'normal';
            titleEl.style.fontStyle = this.state.titleItalic ? 'italic' : 'normal';
            titleEl.style.color = titleColor;
        } else {
            titleEl.style.display = 'none';
        }

        // FIX #1: Use innerHTML for dimension display so HTML entities render properly
        if (infoEl) {
            const dimLabel = sizeConfig?.label || '3" \u00d7 1\u00bd"';
            const shapeLabel = this.state.shape === 'oval' ? 'Oval' : 'Rectangle';
            const cornersLabel = (this.state.shape === 'rectangle' && this.state.corners === 'rounded') ? ' \u2022 Rounded' : '';
            const modeLabel = this.state.mode === 'fullcolor' ? ' \u2022 Full Color' : '';
            infoEl.innerHTML = `${dimLabel} &bull; ${shapeLabel}${cornersLabel}${modeLabel}`;
        }
    }

    // Helper to convert hex color to CSS filter
    getColorFilter(hex) {
        hex = hex.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        
        if (r > 200 && g > 200 && b > 200) {
            return 'brightness(0) invert(1)';
        }
        if (hex.toLowerCase() === '5d4037') {
            return 'brightness(0) saturate(100%) invert(25%) sepia(30%) saturate(500%) hue-rotate(350deg) brightness(95%)';
        }
        return 'brightness(0) saturate(100%)';
    }

    // === PRICING ===
    updatePrice() {
        // For full color, always use rectangle pricing
        const pricingShape = this.state.mode === 'fullcolor' ? 'rectangle' : this.state.shape;
        const pricing = this.config.base_pricing?.[this.state.size]?.[pricingShape] || [0, 0, 0, 0];
        const breaks = this.config.quantity_breaks || [1, 6, 26, 100];
        const q = this.state.quantity;

        let tierIdx = 0;
        if (q >= breaks[3]) tierIdx = 3;
        else if (q >= breaks[2]) tierIdx = 2;
        else if (q >= breaks[1]) tierIdx = 1;

        let unit = pricing[tierIdx] || pricing[0] || 0;

        // Add-ons
        // For full color, rounded corners are included (automatic), no extra charge
        if (this.state.mode !== 'fullcolor' && this.state.shape === 'rectangle' && this.state.corners === 'rounded') {
            unit += this.config.add_ons?.rounded_corners || 0;
        }
        if (this.state.mode === 'fullcolor') {
            unit += this.config.add_ons?.full_color || 0;
            // Rounded corners are INCLUDED with full color, no separate charge
        }
        const fastener = this.config.fasteners?.find(f => f.id === this.state.fastener);
        if (fastener) unit += fastener.price_add || 0;

        this.state.unitPrice = unit;
        this.state.totalPrice = unit * q;

        const unitDisp = this.modal?.querySelector('#unitPriceDisplay');
        const totalDisp = this.modal?.querySelector('#totalPriceDisplay');
        if (unitDisp) unitDisp.textContent = '$' + unit.toFixed(2);
        if (totalDisp) totalDisp.textContent = '$' + this.state.totalPrice.toFixed(2);
    }

    // === GENERATE PREVIEW IMAGE ===
    async generatePreviewImage() {
        const preview = this.modal?.querySelector('#badgePreview');
        if (!preview) return null;

        this.isCapturing = true;

        try {
            // METHOD 1: html2canvas (preferred, matches what we use in other customizers)
            if (typeof html2canvas !== 'undefined') {
                try {
                    // Clone the preview into an isolated container to avoid capturing other page elements
                    const tempContainer = document.createElement('div');
                    tempContainer.style.cssText = 'position:fixed;top:-9999px;left:-9999px;z-index:-1;';
                    document.body.appendChild(tempContainer);

                    const clone = preview.cloneNode(true);
                    // Copy computed styles for the clone
                    clone.style.width = preview.offsetWidth + 'px';
                    clone.style.height = preview.offsetHeight + 'px';
                    clone.style.borderRadius = preview.style.borderRadius;
                    clone.style.overflow = 'hidden';
                    tempContainer.appendChild(clone);

                    // Wait for images to settle
                    await new Promise(r => setTimeout(r, 150));

                    const canvas = await html2canvas(tempContainer, {
                        backgroundColor: null,
                        scale: 2,
                        logging: false,
                        useCORS: true,
                        allowTaint: true,
                        width: preview.offsetWidth,
                        height: preview.offsetHeight,
                        ignoreElements: (el) => el.tagName === 'SCRIPT'
                    });

                    document.body.removeChild(tempContainer);

                    const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
                    console.log('Preview generated via html2canvas, size:', dataUrl.length);

                    // Shopify has a ~65KB limit on cart line item properties
                    if (dataUrl.length < 65000) {
                        return dataUrl;
                    } else {
                        console.log('html2canvas image too large, using lower quality...');
                        const smallerUrl = canvas.toDataURL('image/jpeg', 0.3);
                        if (smallerUrl.length < 65000) return smallerUrl;
                        console.log('Still too large, falling back to canvas method');
                    }
                } catch (e) {
                    console.error('html2canvas error:', e);
                }
            }

            // METHOD 2: Manual canvas fallback (always works, no external dependency)
            return this.generateCanvasPreview();

        } finally {
            this.isCapturing = false;
        }
    }

    // Fallback preview generator using manual canvas drawing
    generateCanvasPreview() {
        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = 300;
            canvas.height = 150;

            const sizeConfig = this.config.sizes?.find(s => s.id === this.state.size);
            const aspect = (sizeConfig?.width || 3) / (sizeConfig?.height || 1.5);
            let drawW = 280, drawH = drawW / aspect;
            if (drawH > 130) { drawH = 130; drawW = drawH * aspect; }
            const x = (300 - drawW) / 2, y = (150 - drawH) / 2;

            // Background
            if (this.state.mode === 'engraved') {
                ctx.fillStyle = this.state.engravedColor?.surface_hex || '#D4AF37';
            } else {
                ctx.fillStyle = this.state.fullColorBgSolid || '#FFFFFF';
            }

            // Draw badge shape
            const r = this.state.corners === 'rounded' ? 12 : 0;
            if (this.state.shape === 'oval') {
                ctx.beginPath();
                ctx.ellipse(x + drawW/2, y + drawH/2, drawW/2, drawH/2, 0, 0, Math.PI * 2);
                ctx.fill();
            } else {
                ctx.beginPath();
                ctx.roundRect(x, y, drawW, drawH, r);
                ctx.fill();
            }

            // Border
            ctx.strokeStyle = 'rgba(0,0,0,0.2)';
            ctx.lineWidth = 1;
            if (this.state.shape === 'oval') {
                ctx.beginPath();
                ctx.ellipse(x + drawW/2, y + drawH/2, drawW/2, drawH/2, 0, 0, Math.PI * 2);
                ctx.stroke();
            } else {
                ctx.beginPath();
                ctx.roundRect(x, y, drawW, drawH, r);
                ctx.stroke();
            }

            // Text
            const textColor = this.state.mode === 'engraved' ? (this.state.engravedColor?.engrave_hex || '#000') : this.state.nameColor;
            ctx.fillStyle = textColor;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            const nameText = this.state.name || 'Your Name';
            const nameFontName = this.state.nameFont.split(',')[0].replace(/'/g, '');
            ctx.font = `${this.state.nameBold ? 'bold ' : ''}${Math.min(this.state.nameSize, 24)}px ${nameFontName}, Arial`;
            
            const centerY = y + drawH / 2;
            if (this.state.title) {
                ctx.fillText(nameText, 150, centerY - 10, drawW - 20);
                const titleFontName = this.state.titleFont.split(',')[0].replace(/'/g, '');
                ctx.fillStyle = this.state.mode === 'engraved' ? (this.state.engravedColor?.engrave_hex || '#000') : this.state.titleColor;
                ctx.font = `${this.state.titleBold ? 'bold ' : ''}${Math.min(this.state.titleSize, 16)}px ${titleFontName}, Arial`;
                ctx.fillText(this.state.title, 150, centerY + 12, drawW - 20);
            } else {
                ctx.fillText(nameText, 150, centerY, drawW - 20);
            }

            return canvas.toDataURL('image/jpeg', 0.6);
        } catch (e) {
            console.error('Canvas preview fallback error:', e);
            return null;
        }
    }

    // === ADD TO CART ===
    async addToCart() {
        if (!this.state.name && this.state.bulkMode === 'identical') {
            alert('Please enter a name for your badge.');
            return;
        }

        const btn = this.modal.querySelector('#addToCartBtn');
        btn.disabled = true;
        btn.textContent = 'Processing...';
        const processingOverlay = this.modal.querySelector('#lw-processing-overlay');
        if (processingOverlay) processingOverlay.classList.add('active');

        // Refresh variant ID
        const freshVariantId = this.getVariantId();
        
        console.log('=== ADD TO CART DEBUG ===');
        console.log('Config variant ID:', this.config.variantId);
        console.log('Fresh variant ID:', freshVariantId);
        console.log('Unit price:', this.state.unitPrice);
        console.log('Total price:', this.state.totalPrice);
        console.log('=========================');
        
        if (!freshVariantId) {
            alert('Error: Could not find product variant. Please refresh the page.');
            btn.disabled = false;
            btn.textContent = 'Add to Cart';
            if (processingOverlay) processingOverlay.classList.remove('active');
            return;
        }

        const sizeConfig = this.config.sizes?.find(s => s.id === this.state.size);
        const colorName = this.state.mode === 'engraved' ? this.state.engravedColor?.name : 'Full Color';
        const fastener = this.config.fasteners?.find(f => f.id === this.state.fastener);

        // Generate preview image FIRST
        let previewUrl = null;
        try {
            previewUrl = await this.generatePreviewImage();
            console.log('Preview generated:', previewUrl ? `Success (${previewUrl.length} chars)` : 'Failed - using fallback');
        } catch (e) {
            console.error('Preview generation error:', e);
        }

        // Switch overlay to loading
        if (processingOverlay) processingOverlay.classList.remove('active');
        const loadingOverlay = document.getElementById('lw-loading');
        if (loadingOverlay) loadingOverlay.classList.add('active');

        // Build FormData
        const fd = new FormData();
        fd.append('id', freshVariantId);
        fd.append('quantity', this.state.quantity);

        // Properties
        fd.append('properties[Customized]', 'Yes');
        fd.append('properties[Size]', sizeConfig?.label || this.state.size);
        fd.append('properties[Shape]', this.state.shape === 'oval' ? 'Oval' : 'Rectangle');
        if (this.state.shape === 'rectangle') {
            fd.append('properties[Corners]', this.state.corners === 'rounded' ? 'Rounded' : 'Square');
        }
        fd.append('properties[Badge Type]', this.state.mode === 'engraved' ? 'Engraved' : 'Full Color');
        fd.append('properties[Color/Material]', colorName);
        fd.append('properties[Layout]', this.config.layouts?.find(l => l.id === this.state.layout)?.name || this.state.layout);
        fd.append('properties[Fastener]', fastener?.name || 'Pin');

        // Text info
        fd.append('properties[Name Font]', this.state.nameFont.split(',')[0].replace(/'/g, ''));
        fd.append('properties[Name Size]', this.state.nameSize + 'pt');

        if (this.state.bulkMode === 'identical') {
            fd.append('properties[Name]', this.state.name);
            if (this.state.title) fd.append('properties[Title]', this.state.title);
        } else {
            fd.append('properties[Variable Data]', 'Yes');
            this.state.bulkData.forEach((row, i) => {
                if (row.name) {
                    fd.append(`properties[Badge ${i + 1}]`, row.name + (row.title ? ' | ' + row.title : ''));
                }
            });
        }

        if (this.state.hasLogo) {
            fd.append('properties[Logo]', 'Customer Provided');
        }

        if (this.state.mode === 'fullcolor') {
            if (this.state.fullColorBgType === 'gradient') {
                const g = this.state.fullColorGradient;
                fd.append('properties[Background]', `${g.type} gradient`);
            } else if (this.state.fullColorBgType === 'image') {
                fd.append('properties[Background]', 'Image: ' + (this.state.fullColorBgImage?.name || 'Custom'));
            } else {
                fd.append('properties[Background]', 'Solid: ' + this.state.fullColorBgSolid);
            }
        }

        // Price properties for cart-addon-pricing.js
        // _addon_price must be in CENTS as a string - this is what cart-addon-pricing.js reads
        const addonPriceCents = Math.round(this.state.unitPrice * 100);
        fd.append('properties[_addon_price]', String(addonPriceCents));
        
        // Build surcharge breakdown for cart display
        const breakdownParts = [];
        const pricingShape = this.state.mode === 'fullcolor' ? 'rectangle' : this.state.shape;
        const basePricing = this.config.base_pricing?.[this.state.size]?.[pricingShape] || [0];
        const breaks = this.config.quantity_breaks || [1, 6, 26, 100];
        let tierIdx = 0;
        if (this.state.quantity >= breaks[3]) tierIdx = 3;
        else if (this.state.quantity >= breaks[2]) tierIdx = 2;
        else if (this.state.quantity >= breaks[1]) tierIdx = 1;
        const baseUnit = basePricing[tierIdx] || basePricing[0] || 0;
        breakdownParts.push(`Base: $${baseUnit.toFixed(2)}`);
        
        if (this.state.mode !== 'fullcolor' && this.state.shape === 'rectangle' && this.state.corners === 'rounded') {
            breakdownParts.push(`Rounded Corners: +$${(this.config.add_ons?.rounded_corners || 0.50).toFixed(2)}`);
        }
        if (this.state.mode === 'fullcolor') {
            breakdownParts.push(`Full Color: +$${(this.config.add_ons?.full_color || 2.00).toFixed(2)}`);
        }
        const fastenerAddon = this.config.fasteners?.find(f => f.id === this.state.fastener);
        if (fastenerAddon && fastenerAddon.price_add > 0) {
            breakdownParts.push(`${fastenerAddon.name}: +$${fastenerAddon.price_add.toFixed(2)}`);
        }
        fd.append('properties[_surcharge_breakdown]', breakdownParts.join(', '));
        
        // Also store readable prices for admin reference (underscore hides from customer)
        fd.append('properties[_Unit Price]', '$' + this.state.unitPrice.toFixed(2));
        fd.append('properties[_Total Price]', '$' + this.state.totalPrice.toFixed(2));

        // Add preview image (lowercase _preview to match your cart template)
        if (previewUrl && previewUrl.length < 65000) {
            fd.append('properties[_preview]', previewUrl);
            console.log('Preview image added to cart properties');
        } else if (previewUrl) {
            console.warn('Preview image too large for cart properties:', previewUrl.length, 'chars');
        }

        try {
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                body: fd,
                headers: { 'X-Requested-With': 'XMLHttpRequest' }
            });

            if (response.ok) {
                const cartData = await response.json();
                console.log('Successfully added to cart:', cartData);
                setTimeout(() => { window.location.href = '/cart'; }, 500);
            } else if (response.status === 422) {
                // 422 can happen but item might still be added
                console.log('Got 422, checking cart...');
                await new Promise(r => setTimeout(r, 500));
                const cartCheck = await fetch('/cart.js');
                const cart = await cartCheck.json();
                const found = cart.items?.some(item => 
                    item.properties?.['Customized'] === 'Yes' && 
                    String(item.variant_id) === String(freshVariantId)
                );
                if (found) {
                    console.log('Item found in cart despite 422');
                    setTimeout(() => { window.location.href = '/cart'; }, 500);
                } else {
                    throw new Error('Item not added to cart. The product variant may be unavailable.');
                }
            } else {
                const err = await response.json().catch(() => ({ description: 'Unknown error' }));
                throw new Error(err.description || 'Failed to add to cart');
            }
        } catch (err) {
            console.error('Add to cart error:', err);
            alert('Error adding to cart: ' + err.message + '\n\nPlease try refreshing the page.');
            btn.disabled = false;
            btn.textContent = 'Add to Cart';
            if (processingOverlay) processingOverlay.classList.remove('active');
            if (loadingOverlay) loadingOverlay.classList.remove('active');
        }
    }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
    if (window.lw_namebadge_config) {
        window.nameBadgeCustomizer = new NameBadgeCustomizer();
    }
});

if (document.readyState !== 'loading' && window.lw_namebadge_config) {
    window.nameBadgeCustomizer = new NameBadgeCustomizer();
}